import {put, call, all} from 'redux-saga/effects';
import CaseTypeStage from "../../entities/caseTypeStage";
import * as Actions from "../actions/caseTypeStageActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CaseTypeStageAction = {
    caseTypeStageId: string;
    caseTypeStage: CaseTypeStage;
    caseTypeStages: CaseTypeStage[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCaseTypeStage(action: CaseTypeStageAction) {
	const session = tabSession.captureGeneration();
	try {
		const caseTypeStage: CaseTypeStage = yield call(api.findOne, 'caseTypeStage', action.caseTypeStageId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_FETCH_ONE_SUCCESS, caseTypeStageId: action.caseTypeStageId, caseTypeStage, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_FETCH_ONE_FAILURE, caseTypeStageId: action.caseTypeStageId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CaseTypeStageAction} action
 */
export function* saveCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CASETYPESTAGE_SAVE saga', action.caseTypeStage?.id);

		if (action.caseTypeStage === undefined || action.caseTypeStage === null) {
			console.error('saveCaseTypeStage received a null');
			throw new Error('saveCaseTypeStage received a null');
		}

		if (!(action.caseTypeStage instanceof CaseTypeStage)) {
			action.caseTypeStage = new CaseTypeStage(action.caseTypeStage);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.caseTypeStage.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.caseTypeStage);
			const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
			if (addResult.isErr()) {
				console.error(`⮂ saveCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCaseTypeStageSave(action.caseTypeStage, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CASETYPESTAGE_SAVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CASETYPESTAGE_SAVE, caseTypeStage: action.caseTypeStage, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCaseTypeStageSave(action.caseTypeStage, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CASETYPESTAGE_SAVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CaseTypeStageAction} action
 * @param {CaseTypeStage[]} action.caseTypeStages
 */
export function* saveManyCaseTypeStages(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CASETYPESTAGE_SAVE_MANY saga', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));

        if (!Array.isArray(action.caseTypeStages)) {
            console.error('saveManyCaseTypeStages expected an array, received', action.caseTypeStages);
            throw new Error('saveManyCaseTypeStages expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCaseTypeStage), under these queue keys
		const dispatchedAddedOns: number[] = action.caseTypeStages.map(caseTypeStage => caseTypeStage?.addedOn);
		const revertAll = () => action.caseTypeStages.map((caseTypeStage, i) =>
			put(Actions.revertCaseTypeStageSave(caseTypeStage, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: CaseTypeStage[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.caseTypeStages.length; i++) {
				const caseTypeStage = action.caseTypeStages[i];
				caseTypeStage.persisted = false;
				ensureOperationId(caseTypeStage);
				toSave.push(caseTypeStage);
			}

			const addResult = yield call(idb.add, 'caseTypeStage', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCaseTypeStages: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CASETYPESTAGE_SAVE_MANY_FAILURE, caseTypeStages: action.caseTypeStages, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CASETYPESTAGE_SAVE_MANY, caseTypeStages: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CASETYPESTAGE_SAVE_MANY_FAILURE, caseTypeStages: action.caseTypeStages, status: 500, err});

			console.error(`Error saving ${action.caseTypeStages.length} caseTypeStages to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CaseTypeStageAction} action
 */
export function* removeCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.caseTypeStage === undefined || action.caseTypeStage === null || !(action.caseTypeStage instanceof CaseTypeStage)) {
			console.error('removeCaseTypeStage expected type CaseTypeStage, received', action.caseTypeStage);
			reportQueued(false);
			throw new Error('removeCaseTypeStage expected type CaseTypeStage');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.caseTypeStage.status = 'D';
		action.caseTypeStage.addedOn = newHRTimestamp();
		action.caseTypeStage.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.caseTypeStage);
		const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CASETYPESTAGE_REMOVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_REMOVE, caseTypeStage: action.caseTypeStage});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_REMOVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this CaseTypeStage.
	*
	* @param {CaseTypeStageAction} action
	*/
export function* restoreCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCaseTypeStage expected getCurrentUserId() to return a value');
        return;
    }

	if (action.caseTypeStage === undefined || action.caseTypeStage === null) {
        console.error('restoreCaseTypeStage expected a CaseTypeStage, received null');
        throw new Error('restoreCaseTypeStage expected a CaseTypeStage, received null');
    }
    else if (!(action.caseTypeStage instanceof CaseTypeStage)) action.caseTypeStage = new CaseTypeStage(action.caseTypeStage); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.caseTypeStage.status = 'U';
		action.caseTypeStage.addedOn = newHRTimestamp();
		action.caseTypeStage.addedBy = userId;
		action.caseTypeStage.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.caseTypeStage);
		const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPESTAGE_RESTORE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_RESTORE, caseTypeStage: action.caseTypeStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_RESTORE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

		console.error('Error restoring from database', err);
	}
}